import {Button} from "primereact/button";
import {Message} from "primereact/message";
import {Tooltip} from "primereact/tooltip";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";

import {lifeCycleStateLabelKey} from "@reva/shared/lifecycle-state";
import {SnapshotType} from "@reva/shared/valuation-data";

import {AttachInspectionOrderDialog} from "../../component/AttachInspectionOrderDialog";
import {CommissionValueOrderDialog} from "../../component/commissionOrder/CommissionValueOrderDialog";
import {DeleteValuationDialog} from "../../component/DeleteValuationDialog";
import {DetailsPanel, DetailsPanelRow, DetailsPanelSeparator} from "../../component/panels";
import {useRevaTranslation} from "../../hooks/languageHooks";
import {
    useCanWriteValuationContent,
    useCheckPermission,
    useCheckPermissionAdmin,
    useIsRevisionReadOnly,
} from "../../hooks/permissionHooks";
import {useUnloadValuation} from "../../hooks/valuationLoadHooks";
import {useCommissionEntitlements} from "../../hooks/valueOrderEntitlementsHooks";
import {selectRevaApiFullUrl} from "../../redux/envSlice";
import {
    selectImmobilienId,
    selectInitialValuationExecuted,
    selectLifeCycleStateFinal,
    selectRevaId,
    selectSnapshots,
    selectValuationMetadata,
} from "../../redux/persistenceSlice";
import {deleteValuation, deleteValuationAndOrder} from "../../redux/persistenceThunk";
import {useRevaDispatch} from "../../redux/revaHooks";
import {selectHasInspectionOrder} from "../../redux/valueOrdersSelectors";
import {selectHasValueOrders, selectRevaOrder} from "../../redux/valueOrdersSlice";
import {formatDateAndTime} from "../../util/dateUtil";
import {PERMISSION_RULE_DELETION} from "../../util/permissions";
import {RevaPermission} from "../../util/revaPermission";
import {AssignUserDropdown} from "./AssignUserDropdown";
import {CommissionEntitlementsNotice} from "./CommissionEntitlementsNotice";
import OrderReloadButton from "./OrderReloadButton";

const ACCOUNT_MANAGEMENT_URL = "https://accounts.immobrain.net";

interface UserLinkProps {
    user: string;
    fullName?: string;
}

function UserLink({user, fullName}: UserLinkProps) {
    const isAdmin = useCheckPermissionAdmin();
    const displayName = fullName || user;
    if (!isAdmin) {
        return <>{displayName}</>;
    }
    return (
        <a href={`${ACCOUNT_MANAGEMENT_URL}/editUser.html?username=${user}`} target="_blank" rel="noreferrer">
            {displayName}
        </a>
    );
}

interface LicenseLinkProps {
    licenseId: number;
    licenseName: string;
}

function LicenseLink({licenseId, licenseName}: LicenseLinkProps) {
    const isAdmin = useCheckPermissionAdmin();
    if (!isAdmin) {
        return <>{licenseName}</>;
    }
    return (
        <a href={`${ACCOUNT_MANAGEMENT_URL}/editLicense.html?licenseId=${licenseId}`} target="_blank" rel="noreferrer">
            {licenseName} (id: {licenseId})
        </a>
    );
}

/**
 * Debug link (new tab) to the raw JSON export of this valuation in the REVA-REST API.
 * Opens the full external API URL directly; the REVA-REST server requests Basic-Auth.
 */
function ExportJsonLink({revaId}: {revaId: string}) {
    const {t} = useTranslation();
    const revaApiFullUrl = useSelector(selectRevaApiFullUrl);
    const href = `${revaApiFullUrl}/platform/exportJson/${revaId}`;
    return (
        <>
            <a
                href={href}
                target="_blank"
                rel="noreferrer"
                className="export-json-link InfoCardHeaderAction"
                data-pr-tooltip={t("order.revaOrder.exportJsonTooltip")}
            >
                <span className="pi pi-file-export" />
            </a>
            <Tooltip target=".export-json-link" position="left" />
        </>
    );
}

export function PanelRevaOrder() {
    const {t, language} = useRevaTranslation();
    const metadata = useSelector(selectValuationMetadata);
    const isFinal = useSelector(selectLifeCycleStateFinal);
    const canDelete = useCheckPermission(PERMISSION_RULE_DELETION);
    const revaId = useSelector(selectRevaId);
    const immobilienId = useSelector(selectImmobilienId);
    const revaOrder = useSelector(selectRevaOrder);
    const hasValueOrders = useSelector(selectHasValueOrders);
    const hasInspectionOrder = useSelector(selectHasInspectionOrder);
    const isRevisionReadOnly = useIsRevisionReadOnly();
    const canWriteContent = useCanWriteValuationContent();
    const canExport = useCheckPermission(RevaPermission.REVA_EXPORT_ANY_VALUATION_FOR_LICENSE);
    const snapshots = useSelector(selectSnapshots);
    const initialValuationExecuted = useSelector(selectInitialValuationExecuted);
    const dispatch = useRevaDispatch();
    const unloadValuation = useUnloadValuation();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [attachInspectionDialogOpen, setAttachInspectionDialogOpen] = useState(false);
    const [commissionValueOrderDialogOpen, setCommissionValueOrderDialogOpen] = useState(false);

    // Ohne Client-ID der eigenen Lizenz lehnt der Server das Beauftragen erst beim Absenden ab -
    // daher hier schon prüfen, statt nur auf die Order-GUID zu schauen. Eigene Bedingung statt
    // Verschachtelung im Aktionsblock, damit sie nicht an canDelete/canAttachInspectionOrder hängt.
    // Steht vor dem frühen Return, weil der Hook darunter sie braucht. Wie Verknüpfen nur, wenn der User die
    // Bewertung in ihrem Zustand inhaltlich bearbeiten darf (#19302 V-B8).
    const canCommissionValueOrder = !!revaOrder?.valueClientGuid && !!metadata?.licenseValueClientId && canWriteContent;
    // Freischaltungen beim ValueAG-Auftraggeber der eigenen Lizenz: blockiert (Hinweis statt Button) oder schränkt
    // die Auftragsarten ein.
    const commissionEntitlements = useCommissionEntitlements(canCommissionValueOrder);

    if (!metadata) {
        return null;
    }

    // JSON-Export-Link nur zeigen, wenn berechtigt UND der Export Daten liefern würde
    // (InitialValuation vorhanden oder ein Final-/PreFinal-Snapshot existiert).
    const hasExportData =
        !!initialValuationExecuted ||
        (snapshots ?? []).some((s) => s.type === SnapshotType.Final || s.type === SnapshotType.PreFinal);
    const exportRevaId = canExport && hasExportData ? revaId : null;

    const valueOrderGuid = revaOrder?.valueOrderGuid;
    const licenseName = metadata?.licenseDisplayName ?? metadata?.licenseTitle;
    const licenseValueClientId = metadata?.licenseValueClientId;
    const owner = metadata?.ownerFullName ?? metadata?.owner ?? t("order.owner.empty");

    // Verknüpfen setzt serverseitig eine Client-ID der eigenen Lizenz voraus (sonst "Funktion nicht
    // verfügbar" erst beim Absenden) und das inhaltliche Schreibrecht im aktuellen Zustand (#19302 V-B8).
    const canAttachInspectionOrder = !hasInspectionOrder && canWriteContent && !!licenseValueClientId;

    const renderOwner = () => {
        // Nur finalisierte Bewertungen: Anzeige als UserLink – kein Dropdown. Der Owner bezieht sich auf
        // den Live-Zustand der Bewertung (wird NICHT mit einem Snapshot eingefroren) und ist daher
        // änderbar, solange die Bewertung nicht final ist – auch beim Betrachten eines Snapshots.
        // Revision-readonly-Rolle darf ebenfalls nicht zuweisen (reva-rest lehnt ASSIGN für sie
        // grundsätzlich ab, s. LifeCycleGuards) – ohne diesen Guard würde der Dropdown angezeigt,
        // aber jede Auswahl mit 403 scheitern.
        if (isFinal || isRevisionReadOnly) {
            return metadata?.owner ? (
                <UserLink user={metadata.owner} fullName={metadata.ownerFullName} />
            ) : (
                t("order.owner.empty")
            );
        }
        // Nicht-finalisierte Bewertungen: Dropdown zum Zuweisen
        return (
            <AssignUserDropdown
                revaId={revaId}
                currentOwner={metadata?.owner}
                currentOwnerFullName={metadata?.ownerFullName}
            />
        );
    };

    const deleteOrder = async (withOrder: boolean, reason?: string, explanation?: string) => {
        if (!revaId) {
            return;
        }
        // Grund und Erläuterung sind nur bei "mit Auftrag" gesetzt: der Dialog gibt diese Schaltfläche
        // erst frei, wenn beide ausgefüllt sind.
        const result = withOrder
            ? await dispatch(deleteValuationAndOrder(revaId, reason!, explanation!))
            : await dispatch(deleteValuation(revaId));
        if (result?.revaId != null) {
            await unloadValuation("/");
        }
    };

    const renderDateTime = (value: string) => formatDateAndTime(value, language);
    const renderLicense = (licenseId: number) => (licenseName: string) => (
        <LicenseLink licenseId={licenseId} licenseName={licenseName} />
    );
    const renderUser = (fullName?: string) => (user: string) => <UserLink user={user} fullName={fullName} />;

    return (
        <DetailsPanel
            icon={"pi pi-id-card"}
            title={t("order.revaOrder.title")}
            headerActions={
                <>
                    <OrderReloadButton />
                    {exportRevaId ? <ExportJsonLink revaId={exportRevaId} /> : null}
                </>
            }
        >
            <DetailsPanelRow
                label={t("order.revaOrder.license")}
                value={licenseName}
                renderValue={renderLicense(metadata?.licenseId)}
            />
            <DetailsPanelRow label={t("order.revaOrder.licenseValueClientId")} value={licenseValueClientId} />
            <DetailsPanelRow label={t("order.revaOrder.immobilienId")} value={immobilienId} />
            <DetailsPanelRow
                label={t("order.revaOrder.creationDate")}
                renderValue={renderDateTime}
                value={metadata?.creationDate}
            />
            <DetailsPanelRow
                label={t("order.revaOrder.createdBy")}
                value={metadata?.creationUser}
                renderValue={renderUser(metadata?.creationUserFullName)}
            />
            <DetailsPanelRow
                label={t("order.revaOrder.lifeCycleState")}
                value={metadata?.lifeCycleState}
                renderValue={(state) => t(lifeCycleStateLabelKey(state))}
            />
            <DetailsPanelRow
                label={t("order.revaOrder.finalizationDate")}
                renderValue={renderDateTime}
                value={metadata?.finalizeDate}
            />
            <DetailsPanelRow
                label={t("order.revaOrder.finalizedBy")}
                value={metadata?.finalizeUser}
                renderValue={renderUser(metadata?.finalizeUserFullName)}
            />
            <DetailsPanelRow
                label={t("order.revaOrder.lastUpdateDate")}
                renderValue={renderDateTime}
                value={metadata?.lastUpdateDate}
            />
            <DetailsPanelRow
                label={t("order.revaOrder.lastUpdatedBy")}
                value={metadata?.lastUpdateUser}
                renderValue={renderUser(metadata?.lastUpdateUserFullName)}
            />
            <DetailsPanelRow
                label={t("order.revaOrder.owner")}
                value={owner}
                className="mb-1 items-center"
                renderValue={renderOwner}
            />
            {!!revaOrder && (
                <>
                    <DetailsPanelSeparator />
                    <DetailsPanelRow
                        label={t("order.order.externalOrderNumber")}
                        value={revaOrder?.valueExternalOrderNumber}
                    />
                    <DetailsPanelRow label={t("order.order.orderGuid")} value={valueOrderGuid} />
                </>
            )}
            {(canDelete || canAttachInspectionOrder || canCommissionValueOrder) && (
                <>
                    <DetailsPanelSeparator />

                    {canAttachInspectionOrder && (
                        <>
                            <Button
                                label={t("order.revaOrder.attachInspectionOrder")}
                                className="w-full mb-4"
                                onClick={() => setAttachInspectionDialogOpen(true)}
                            />
                            <AttachInspectionOrderDialog
                                visible={attachInspectionDialogOpen}
                                onClose={() => setAttachInspectionDialogOpen(false)}
                            />
                        </>
                    )}

                    {canCommissionValueOrder && commissionEntitlements.mode === "blocked" && (
                        <CommissionEntitlementsNotice evaluation={commissionEntitlements} />
                    )}
                    {canCommissionValueOrder && commissionEntitlements.mode !== "blocked" && (
                        <>
                            <Button
                                label={t("order.revaOrder.commissionValueOrder")}
                                className="w-full mb-4"
                                disabled={commissionEntitlements.mode === "unavailable"}
                                onClick={() => setCommissionValueOrderDialogOpen(true)}
                            />
                            {commissionEntitlements.mode === "unavailable" && (
                                <Message
                                    id="commissionEntitlementsUnavailable"
                                    severity="info"
                                    text={t("order.revaOrder.entitlements.unavailable")}
                                    className="w-full mb-4 justify-start"
                                />
                            )}
                            <CommissionValueOrderDialog
                                visible={commissionValueOrderDialogOpen}
                                onClose={() => setCommissionValueOrderDialogOpen(false)}
                                allowedOrderTypes={commissionEntitlements.allowedOrderTypes}
                                missingOrderTypes={commissionEntitlements.missingOrderTypes}
                                allowedOrderOptions={commissionEntitlements.allowedOrderOptions}
                            />
                        </>
                    )}

                    {canDelete && (
                        <>
                            <Button
                                label={t("order.revaOrder.delete")}
                                className="w-full"
                                severity="danger"
                                onClick={() => setDeleteDialogOpen(true)}
                            />
                            <DeleteValuationDialog
                                revaId={deleteDialogOpen ? revaId : null}
                                hasValueOrders={hasValueOrders}
                                onCancel={() => setDeleteDialogOpen(false)}
                                onDelete={(withOrder, reason, explanation) =>
                                    void deleteOrder(withOrder, reason, explanation)
                                }
                            />
                        </>
                    )}
                </>
            )}
        </DetailsPanel>
    );
}
